import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { BarChart3, Megaphone, Globe, Save, ExternalLink, User, Languages } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useLanguagePreference, languageOptions } from "@/hooks/useLanguagePreference";
import { normalizeLanguage, type SupportedLanguage } from "@/i18n";
import { useTrialMode } from "@/hooks/useTrialMode";

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { changeLanguage } = useLanguagePreference();
  const trialMode = useTrialMode();

  const [gaId, setGaId] = useState(localStorage.getItem("ga_measurement_id") || "");
  const [adsId, setAdsId] = useState(localStorage.getItem("gads_conversion_id") || "");
  const [adsLabel, setAdsLabel] = useState(localStorage.getItem("gads_conversion_label") || "");
  const [gaEnabled, setGaEnabled] = useState(localStorage.getItem("ga_enabled") === "true");
  const [adsEnabled, setAdsEnabled] = useState(localStorage.getItem("gads_enabled") === "true");
  const [preferredLang, setPreferredLang] = useState(i18n.language);

  // Load language preference from profile
  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("language_preference")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        if (data?.language_preference) {
          setPreferredLang(data.language_preference);
        }
      });
  }, [user]);

  const updatePreferredLanguage = async (language: string) => {
    const nextLanguage = normalizeLanguage(language) ?? "fr";
    setPreferredLang(nextLanguage);
    await changeLanguage(nextLanguage);
  };

  const savePreferences = async () => {
    await changeLanguage((normalizeLanguage(preferredLang) ?? "fr") as SupportedLanguage);
    toast({ title: t("settings.preferencesSaved") });
  };

  const saveAnalytics = () => {
    localStorage.setItem("ga_measurement_id", gaId);
    localStorage.setItem("ga_enabled", String(gaEnabled));
    toast({ title: t("settings.saveAnalytics"), description: gaEnabled ? `Tracking ID: ${gaId}` : "Disabled" });
  };

  const saveAds = () => {
    localStorage.setItem("gads_conversion_id", adsId);
    localStorage.setItem("gads_conversion_label", adsLabel);
    localStorage.setItem("gads_enabled", String(adsEnabled));
    toast({ title: t("settings.saveAdsConfig"), description: adsEnabled ? `Conversion ID: ${adsId}` : "Disabled" });
  };

  const showTrialReminderAgain = () => {
    trialMode.showReminderAgain.mutate(undefined, {
      onSuccess: () => toast({ title: "Trial reminder enabled" }),
      onError: (error: any) => toast({ title: "Could not update trial reminder", description: error?.message, variant: "destructive" }),
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center border-b border-border bg-background/80 backdrop-blur-xl px-6">
        <div>
          <h1 className="text-lg font-semibold text-foreground">{t("settings.title")}</h1>
          <p className="text-xs text-muted-foreground">{t("settings.subtitle")}</p>
        </div>
      </header>

      <div className="p-6 max-w-3xl space-y-6">
        {trialMode.isTrialPackage && (
          <Card className="bg-card border-border">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base text-foreground">Trial mode</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Trial read-only restrictions stay active until this workspace subscribes to a paid package.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center gap-3">
              <Button asChild size="sm">
                <a href="/billing/plans">View Packages</a>
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={showTrialReminderAgain}
                disabled={!trialMode.reminderDismissedByUser || trialMode.showReminderAgain.isPending}
              >
                Show Trial Reminder Again
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Language Preferences */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent">
                <Languages className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base text-foreground">{t("settings.preferences")}</CardTitle>
                <CardDescription className="text-muted-foreground">{t("settings.preferredLanguageDesc")}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground text-xs">{t("settings.preferredLanguage")}</Label>
              <Select value={preferredLang} onValueChange={(value) => void updatePreferredLanguage(value)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {languageOptions.map((o) => (

                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>

                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={savePreferences} size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Save className="h-4 w-4 mr-1.5" /> {t("settings.savePreferences")}
            </Button>
          </CardContent>
        </Card>

        {/* Google Analytics */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent">
                  <BarChart3 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base text-foreground">{t("settings.googleAnalytics")}</CardTitle>
                  <CardDescription className="text-muted-foreground">{t("settings.gaDescription")}</CardDescription>
                </div>
              </div>
              <Switch checked={gaEnabled} onCheckedChange={setGaEnabled} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground text-xs">{t("settings.gaMeasurementId")}</Label>
              <Input
                placeholder="G-XXXXXXXXXX"
                value={gaId}
                onChange={(e) => setGaId(e.target.value)}
                className="bg-secondary border-border font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                Find it in <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5">Google Analytics <ExternalLink className="h-3 w-3" /></a> → Admin → Data Streams
              </p>
            </div>
            <div className="rounded-md border border-border bg-secondary/30 p-3 text-xs leading-5 text-muted-foreground">
              {t("settings.consentNotice")}
            </div>
            <Button onClick={saveAnalytics} size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Save className="h-4 w-4 mr-1.5" /> {t("settings.saveAnalytics")}
            </Button>
          </CardContent>
        </Card>

        {/* Google Ads */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent">
                  <Megaphone className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base text-foreground">{t("settings.googleAds")}</CardTitle>
                  <CardDescription className="text-muted-foreground">{t("settings.adsDescription")}</CardDescription>
                </div>
              </div>
              <Switch checked={adsEnabled} onCheckedChange={setAdsEnabled} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground text-xs">{t("settings.conversionId")}</Label>
              <Input
                placeholder="AW-XXXXXXXXX/XXXXXXXX"
                value={adsId}
                onChange={(e) => setAdsId(e.target.value)}
                className="bg-secondary border-border font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-xs">{t("settings.conversionLabel")}</Label>
              <Input
                placeholder="AbCdEfGhIjKlMn"
                value={adsLabel}
                onChange={(e) => setAdsLabel(e.target.value)}
                className="bg-secondary border-border font-mono text-sm"
              />
            </div>
            <Button onClick={saveAds} size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Save className="h-4 w-4 mr-1.5" /> {t("settings.saveAdsConfig")}
            </Button>
          </CardContent>
        </Card>

        {/* Site & Blog */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent">
                <Globe className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base text-foreground">{t("settings.siteBlog")}</CardTitle>
                <CardDescription className="text-muted-foreground">{t("settings.siteBlogDescription")}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8 text-center">
              <p className="text-sm text-muted-foreground">{t("settings.siteBlogPlaceholder")}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
